import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Dialog, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { isEmpty } from 'lodash';
import { ApiService, CustomPrimaryUser, IndividualQuestionBaseRepresentation, RequestedFile } from 'api';
import { ALLOWED_LIMITED_VIEWER_ROLES_FOR_DOCS } from 'common/constants/user';
import { useInformationRequestStatuses } from 'common/hooks/useInformationRequestStatuses';
import { useStore } from 'common/store';
import { CompanyByFirm } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import { UserFirmPermission } from 'common/types/user';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { MeasurementDate, ReportDate } from 'dashboard409a/services/types';
import { IStandaloneCompaniesPermissions } from 'dashboard409a/states/features/user';
import { manageApiRequest } from 'hooks/apiRequest';
import RequestsContent from 'pages/Firms/Requests/components/RequestContent/RequestContent';
import useGetCompanyMeasurementDateByMD from 'services/hooks/companies/useGetCompanyMeasurementDateByMD/useGetCompanyMeasurementDateByMD';
import useGetCompanyUsers from 'services/hooks/useGetCompanyUsers';
import useGetFirmUsers from 'services/hooks/useGetFirmUsers';
import { Logger } from 'utilities';
import ConfirmRequest from './components/ConfirmRequest';
import { SourceResponsibleSelector } from '../InformationRequestModal/SourceResponsibleSelector';
import { ResponsibleUser } from '../InformationRequestModal/SourceResponsibleSelector/types';
import InformationRequestModalAppBar from './components/InformationRequestModalAppBar/InformationRequestModalAppBar';
import { CreateInformationRequestModalType } from './CreateInformationRequestModal.props';

const Transition = React.forwardRef(
  (props: TransitionProps & { children?: React.ReactElement }, ref: React.Ref<unknown>) => (
    <Slide direction="up" ref={ref} {...props} />
  )
);

const CreateInformationRequestModal = ({
  isOpen,
  setIsOpen,
  currentMeasurementDateId,
  currentCompanyMeasurementDateId,
  is409a,
  currentPageData,
  handleGetCurrentInformationRequests,
  currentCompanyId,
}: CreateInformationRequestModalType) => {
  const { updatePortalLinksAndTask } = useContext(InformationDashboard409AContext);
  const [isSavingModelOpen, setIsSavingModelOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [notify, setNotify] = useState(true);
  const previousUsersLengthRef = useRef(0);

  const [{ companyInfo, firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { informationRequestStatuses, setUpdateTaskList, currentInformationRequests, hasLoaded }
    = useInformationRequestStatuses({
      currentCompany: companyInfo as IStandaloneCompaniesPermissions | undefined,
      currentMeasurementDateId,
      currentCompanyMeasurementDateId,
      is409a,
      reFetch: isOpen,
    });
  const shouldGetFirmUsers = !ALLOWED_LIMITED_VIEWER_ROLES_FOR_DOCS.includes(
    (user?.firms_permissions as unknown as UserFirmPermission[])?.find(permission => permission.id === firmInfo?.id)?.user_firm_role ?? 0
  );
  const { data: firmUsers = [] } = useGetFirmUsers(shouldGetFirmUsers ? firmInfo?.id : undefined, '');
  const { data: companyUsers = [] } = useGetCompanyUsers(currentCompanyId, '');
  const users = isEmpty(companyUsers) ? firmUsers : companyUsers;
  // Company Measurement Date
  const { data: reportDate } = useGetCompanyMeasurementDateByMD({
    measurementDate: {
      id: currentMeasurementDateId,
    },
  });

  const handleClose = useCallback(() => {
    setHasSaved(false);
    setIsSavingModelOpen(false);
    setIsOpen(false);
  }, [setIsOpen]);

  const responsibleUsers = useMemo<Array<ResponsibleUser>>(
    () =>
      users.map((user: CustomPrimaryUser) => ({
        value: user.id!,
        label: `${user.first_name} ${user.last_name}`,
        isPrimaryContact: !!user.is_primary_contact,
      })),
    [users]
  );

  const getPrimaryContact = useCallback(
    () => responsibleUsers.find(userParam => userParam.isPrimaryContact)?.value,
    [responsibleUsers]
  );

  const getCurrentInformationRequestResponsibleUser = useCallback(() => {
    if (currentInformationRequests.length === 0 || currentInformationRequests[0].responsible_users.length === 0) {
      return undefined;
    }
    return currentInformationRequests[0].responsible_users[0].id;
  }, [currentInformationRequests]);

  const getResponsibleUserId = useCallback(
    () => getPrimaryContact() ?? getCurrentInformationRequestResponsibleUser(),
    [getCurrentInformationRequestResponsibleUser, getPrimaryContact]
  );

  const [responsibleUserId, setResponsibleUserId] = useState<number | undefined>(getResponsibleUserId());

  const responsibleUser = useMemo(
    () => responsibleUsers.find(user => user.value === responsibleUserId),
    [responsibleUserId, responsibleUsers]
  );

  useEffect(() => {
    if (is409a) {
      setResponsibleUserId(getResponsibleUserId());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setResponsibleUserId(getResponsibleUserId());
  }, [getResponsibleUserId]);

  // Detect when a new user is added and select it automatically
  useEffect(() => {
    if (responsibleUsers.length > previousUsersLengthRef.current && responsibleUsers.length > 0) {
      const newUser = responsibleUsers[responsibleUsers.length - 1];
      if (newUser?.value) {
        setResponsibleUserId(newUser.value);
      }
    }
    previousUsersLengthRef.current = responsibleUsers.length;
  }, [responsibleUsers]);

  const closeSaveModel = useCallback(() => {
    setIsSavingModelOpen(false);
  }, []);

  const hasCurrentInformationRequests = useMemo(
    () => currentInformationRequests.length === 2,
    [currentInformationRequests]
  );

  // find the category 1 currentInformationRequests and make a map of the progress property, which is a list of objects with ids
  // then do the same with category 2 requests
  // then filter out the questions and files that match ids with the progress property
  const createdFileIdSet = useMemo(
    () =>
      currentInformationRequests
        .filter(request => request.category === 1)
        .map(request => request.progress)
        .flat()
        .reduce((acc, progress) => {
          acc.add(progress.id);
          return acc;
        }, new Set()),
    [currentInformationRequests]
  );

  const createdQuestionIdSet = useMemo(
    () =>
      currentInformationRequests
        .filter(request => request.category === 2)
        .map(request => request.progress)
        .flat()
        .reduce((acc, progress) => {
          acc.add(progress.id);
          return acc;
        }, new Set()),
    [currentInformationRequests]
  );

  //   when we start doing other types of valuations we will make change here
  const primaryInformationRequest = useMemo(
    () => (informationRequestStatuses.length ? informationRequestStatuses[0] : undefined),
    [informationRequestStatuses]
  );

  const filterItems = useCallback(
    (items: RequestedFile[] | IndividualQuestionBaseRepresentation[] | undefined, createdItemIdSet: Set<unknown>) => {
      if (!hasLoaded) {
        return undefined; // return undefined to render the skeleton loader
      }
      return items?.filter(
        (item: RequestedFile | IndividualQuestionBaseRepresentation) => item.id && !createdItemIdSet.has(item.id)
      ) ?? [];
    },
    [hasLoaded]
  );

  const questions = useMemo(
    () => filterItems(primaryInformationRequest?.questions, createdQuestionIdSet),
    [primaryInformationRequest, createdQuestionIdSet, filterItems]
  );

  const files = useMemo(
    () => filterItems(primaryInformationRequest?.files, createdFileIdSet),
    [primaryInformationRequest, createdFileIdSet, filterItems]
  );

  //   convert reportDate to type ReportDate
  const typedReportDate = useMemo<ReportDate>(
    () =>
      ({
        ...reportDate,
        company: reportDate?.company?.id as number,
        measurement_date: reportDate?.measurement_date as MeasurementDate,
      } as ReportDate),
    [reportDate]
  );

  const handleSave = useCallback(async () => {
    if (!primaryInformationRequest?.id) {
      return;
    }
    setIsSaving(true);

    try {
      if (hasCurrentInformationRequests) {
        await manageApiRequest(
          ApiService.apiUpdateInformationRequestStatusUpdate(primaryInformationRequest.id.toString(), {
            id: primaryInformationRequest.id,
            responsible_users: responsibleUserId ? [responsibleUserId] : [],
            send_email: notify,
          })
        );
      } else if (responsibleUserId) {
        await manageApiRequest(
          ApiService.apiCreateInformationRequestUpdate(primaryInformationRequest.id.toString(), {
            responsible_users: [responsibleUserId],
            send_email: notify,
          })
        );
      }

      setHasSaved(true);
      setUpdateTaskList(true);

      // Update portal links and task after creating/updating request
      await updatePortalLinksAndTask();
      
      handleGetCurrentInformationRequests?.();
    } catch (error) {
      Logger.error('Error saving information request:', error);
    } finally {
      setIsSaving(false);
    }
  }, [
    primaryInformationRequest,
    setUpdateTaskList,
    hasCurrentInformationRequests,
    responsibleUserId,
    notify,
    handleGetCurrentInformationRequests,
    updatePortalLinksAndTask,
  ]);

  return (
    <Dialog fullScreen open={isOpen} onClose={handleClose} TransitionComponent={Transition}>
      <InformationRequestModalAppBar
        responsibleUserId={responsibleUserId}
        questions={questions}
        files={files}
        handleClose={handleClose}
        setIsSavingModelOpen={setIsSavingModelOpen}
        hasCurrentInformationRequests={hasCurrentInformationRequests}
        isSaving={isSaving}
      />
      <Container maxWidth={false} sx={{ maxWidth: '1920px' }}>
        <RequestsContent
          documents={files}
          questions={questions}
          setUpdateTaskList={setUpdateTaskList}
          primaryInformationRequestId={primaryInformationRequest?.id}
          isInitialSetup
          currentPageData={currentPageData}
        />
        <SourceResponsibleSelector
          responsibleUser={responsibleUserId}
          setResponsibleUser={setResponsibleUserId}
          responsibleUsers={responsibleUsers}
          currentFirmId={firmInfo?.id}
          currentCompanyId={currentCompanyId}
          currentCompanyInfo={{
            company_id: companyInfo?.id,
            company_name: companyInfo?.name,
          } as CompanyByFirm}
          is409a={is409a}
          setIsSaving={setIsSaving}
          isDisabled={isSaving}
        />
      </Container>
      {reportDate && (
        <ConfirmRequest
          open={isSavingModelOpen}
          onClose={closeSaveModel}
          saving={isSaving}
          hasSaved={hasSaved}
          reportDate={typedReportDate}
          responsibleUser={responsibleUser}
          files={files}
          questions={questions}
          notify={notify}
          setNotify={setNotify}
          onSend={handleSave}
          onFinish={handleClose}
        />
      )}
    </Dialog>
  );
};

export default CreateInformationRequestModal;
